import React, { useEffect, useState } from 'react';
import BaseModal from 'components/atoms/base-modal';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import BaseLoader from 'components/atoms/base-loader';

import ClinicReviewTimeModalMenuBody from './body';
import { ErrorMessage } from './errorMessage';
import { useClinicReviewTimeModalData } from './hooks/useData';
import { useClinicReviewTimeModalSave } from './hooks/useSave';
import { ClinicSelect } from './clinicSelect';

import type { ClinicReviewTimeData } from './hooks/useData';

interface IProps {
  opened: boolean;
  onClose: () => void;
}
const ClinicReviewTimeMenuModal: React.FC<IProps> = (props) => {
  const { opened, onClose } = props;

  const { t } = useTranslation();

  const { data, isLoading, remove, isError, error } =
    useClinicReviewTimeModalData();
  const { save, isLoading: isSaveLoading } = useClinicReviewTimeModalSave();

  const handleClose = () => {
    onClose();
  };

  const [newReviewTimeSeconds, setNewReviewTimeSeconds] = useState<
    undefined | number
  >(undefined);

  const [updateChildrenReviewTime, setUpdateChildrenReviewTime] =
    useState<boolean>(false);

  const [currentClinic, setCurrentClinic] =
    useState<ClinicReviewTimeData | null>(null);

  useEffect(() => {
    if (data) {
      setCurrentClinic(data[0]);
    }
  }, [data?.length]);

  useEffect(() => {
    setNewReviewTimeSeconds(currentClinic?.reviewTimeSeconds ?? undefined);
    setUpdateChildrenReviewTime(false);
  }, [currentClinic]);

  const handleSave = async () => {
    if (!currentClinic) return;
    await save({
      guid: currentClinic?.clinicGuid,
      reviewTimeSeconds:
        newReviewTimeSeconds ?? currentClinic?.reviewTimeSeconds,
      updateChildrenReviewTime,
    });
    setCurrentClinic(null);
    remove();
    onClose();
  };

  return (
    <BaseModal
      title={t('labels.updateClinicReviewTime')}
      open={opened}
      onCancel={onClose}
      withFooter={[
        <BaseButton
          dataCy="cancel-button"
          key="cancel"
          type="secondary"
          label={t('controls.cancel')}
          onClick={handleClose}
          disabled={isSaveLoading}
        />,
        <BaseButton
          dataCy="add-time-button"
          key="add-time-button"
          type="primary"
          label={t('controls.apply')}
          disabled={isLoading || isError}
          loading={isSaveLoading}
          onClick={handleSave}
        />,
      ]}
    >
      {data && (
        <ClinicSelect
          list={data}
          onSelect={(clinicGuid) => {
            setCurrentClinic(
              data.find((item) => item.clinicGuid === clinicGuid) || null,
            );
          }}
          value={currentClinic}
        />
      )}
      {currentClinic && (
        <ClinicReviewTimeModalMenuBody
          childCount={currentClinic.childClinicsCount}
          name={currentClinic.clinicName}
          reviewTimeSeconds={
            newReviewTimeSeconds ?? currentClinic.reviewTimeSeconds
          }
          onReviewTimeChange={setNewReviewTimeSeconds}
          updateChildrenReviewTime={updateChildrenReviewTime}
          setUpdateChildrenReviewTime={setUpdateChildrenReviewTime}
        />
      )}
      {isLoading && <BaseLoader loading />}
      {isError && <ErrorMessage error={error} />}
    </BaseModal>
  );
};

export default ClinicReviewTimeMenuModal;
